import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';

import { cn } from '@/lib/cn';

import { Button, IconButton } from './Button';
import { Modal } from './Modal';

/* ---------------------------------------------------------------- toast --- */

type ToastTone = 'info' | 'success' | 'error';

type Toast = {
  id: number;
  tone: ToastTone;
  message: string;
};

type ToastState = {
  toasts: Toast[];
  push: (message: string, tone?: ToastTone) => void;
  dismiss: (id: number) => void;
};

let nextToastId = 1;

const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (message, tone = 'info') => {
    const id = nextToastId++;
    set((state) => ({ toasts: [...state.toasts, { id, tone, message }] }));
  },
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

/** Fire-and-forget notifications; safe to call outside React. */
export const toast = {
  info: (message: string) => useToastStore.getState().push(message, 'info'),
  success: (message: string) => useToastStore.getState().push(message, 'success'),
  error: (message: string) => useToastStore.getState().push(message, 'error'),
};

const TOAST_ICONS = { info: Info, success: CircleCheck, error: CircleAlert };
const TOAST_TONES = {
  info: 'bg-primary/10 text-primary',
  success: 'bg-success/12 text-success',
  error: 'bg-danger/12 text-danger',
};

function ToastRow({ item }: { item: Toast }) {
  const dismiss = useToastStore((state) => state.dismiss);
  const Icon = TOAST_ICONS[item.tone];

  useEffect(() => {
    const timer = setTimeout(() => dismiss(item.id), item.tone === 'error' ? 7000 : 4000);
    return () => clearTimeout(timer);
  }, [item.id, item.tone, dismiss]);

  return (
    <div
      role="status"
      className="pointer-events-auto flex w-full animate-pop-in items-start gap-3 rounded-2xl border border-line bg-surface p-3.5 shadow-pop sm:w-[22rem]"
    >
      <span
        className={cn(
          'inline-flex size-7 shrink-0 items-center justify-center rounded-full',
          TOAST_TONES[item.tone]
        )}
      >
        <Icon size={16} />
      </span>
      <p className="min-w-0 flex-1 self-center text-[13px] font-medium leading-snug">{item.message}</p>
      <IconButton icon={X} label="Dismiss" size="sm" onClick={() => dismiss(item.id)} />
    </div>
  );
}

export function Toaster() {
  const toasts = useToastStore((state) => state.toasts);
  if (toasts.length === 0) return null;

  return createPortal(
    <div className="pointer-events-none fixed inset-x-4 bottom-24 z-[60] flex flex-col items-center gap-2 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:items-end">
      {toasts.map((item) => (
        <ToastRow key={item.id} item={item} />
      ))}
    </div>,
    document.body
  );
}

/* -------------------------------------------------------------- confirm --- */

type ConfirmRequest = {
  title: string;
  message?: string;
  confirmLabel?: string;
  destructive?: boolean;
};

type ConfirmState = {
  request: (ConfirmRequest & { resolve: (ok: boolean) => void }) | null;
  ask: (request: ConfirmRequest) => Promise<boolean>;
  answer: (ok: boolean) => void;
};

const useConfirmStore = create<ConfirmState>((set, get) => ({
  request: null,
  ask: (request) =>
    new Promise<boolean>((resolve) => {
      // Only one dialog at a time; a second ask supersedes the first.
      get().request?.resolve(false);
      set({ request: { ...request, resolve } });
    }),
  answer: (ok) => {
    get().request?.resolve(ok);
    set({ request: null });
  },
}));

/**
 * Promise-based confirmation, so destructive actions read linearly:
 * `if (!(await confirm({...}))) return;`
 */
export const confirm = (request: ConfirmRequest) => useConfirmStore.getState().ask(request);

export function ConfirmHost() {
  const request = useConfirmStore((state) => state.request);
  const answer = useConfirmStore((state) => state.answer);

  return (
    <Modal
      open={request !== null}
      onClose={() => answer(false)}
      title={request?.title ?? ''}
      width="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => answer(false)}>
            Cancel
          </Button>
          <Button
            variant={request?.destructive ? 'danger' : 'primary'}
            onClick={() => answer(true)}
            data-autofocus
          >
            {request?.confirmLabel ?? 'Confirm'}
          </Button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-muted">{request?.message}</p>
    </Modal>
  );
}
